import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, Image, Eye, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function AdminApprovals() {
  const { token, API } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/approvals`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: filter }
      });
      setSubmissions(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token, filter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const approveSubmission = async (id) => {
    try {
      await axios.put(`${API}/admin/approvals/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchSubmissions();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const rejectSubmission = async (id) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      await axios.put(`${API}/admin/approvals/${id}/reject`, { reason: reason || '' }, { headers: { Authorization: `Bearer ${token}` } });
      fetchSubmissions();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const viewImage = async (path) => {
    try {
      const res = await axios.get(`${API}/files/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      setImageBlob(url);
      setViewingImage(path);
    } catch (e) {
      alert('Failed to load image');
    }
  };

  const statusColors = {
    pending: 'border-amber-500/30 text-amber-400',
    approved: 'border-emerald-500/30 text-emerald-400',
    rejected: 'border-red-500/30 text-red-400',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Quest Approvals</h2>
          <p className="text-xs text-zinc-500 mt-1">{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-[#121214] border-white/10 text-white w-40" data-testid="approval-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-white/10">
            <SelectItem value="pending" className="text-white">Pending</SelectItem>
            <SelectItem value="approved" className="text-white">Approved</SelectItem>
            <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
            <SelectItem value="all" className="text-white">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions List */}
      <div className="space-y-3">
        {submissions.map((sub, i) => (
          <motion.div
            key={sub.id}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="glass-card rounded-xl p-5"
            data-testid={`approval-${i}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-sm font-bold text-white">{sub.quest_title}</h3>
                  <Badge variant="outline" className={`text-[9px] ${statusColors[sub.status]}`}>
                    {sub.status}
                  </Badge>
                  <Badge variant="outline" className="text-[9px] border-purple-500/30 text-purple-400">
                    {sub.quest_type}
                  </Badge>
                  <span className="text-[10px] font-mono text-amber-500">+{sub.quest_points} pts</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mb-2">
                  <span className="text-zinc-300">{sub.user_name}</span>
                  <span>{sub.user_email}</span>
                  <span>{new Date(sub.submitted_at).toLocaleString()}</span>
                </div>

                {sub.notes && (
                  <p className="text-xs text-zinc-400 mb-2 italic">"{sub.notes}"</p>
                )}

                {/* Proof Image */}
                {sub.proof_image_path && (
                  <button
                    onClick={() => viewImage(sub.proof_image_path)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-zinc-400 transition-colors mb-2"
                    data-testid={`view-proof-${i}`}
                  >
                    <Image className="w-3.5 h-3.5" />
                    View Proof Image
                  </button>
                )}

                {/* Receipt Data */}
                {sub.receipt_data && sub.receipt_data.success && (
                  <button
                    onClick={() => setViewingReceipt(sub.receipt_data)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 text-xs text-purple-400 transition-colors mb-2"
                    data-testid={`view-receipt-${i}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    View Receipt Analysis
                  </button>
                )}

                {sub.admin_notes && (
                  <p className="text-[10px] text-zinc-600 mt-1">Admin: {sub.admin_notes}</p>
                )}
              </div>

              {/* Actions */}
              {sub.status === 'pending' && (
                <div className="flex items-center gap-2 ml-3">
                  <button
                    onClick={() => approveSubmission(sub.id)}
                    className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-bold flex items-center gap-1.5 border border-emerald-500/20"
                    data-testid={`approve-${i}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => rejectSubmission(sub.id)}
                    className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs font-bold flex items-center gap-1.5 border border-red-500/20"
                    data-testid={`reject-${i}`}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {submissions.length === 0 && !loading && (
        <div className="text-center py-16 glass-card rounded-xl">
          <Clock className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No {filter !== 'all' ? filter : ''} submissions</p>
        </div>
      )}

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewingImage} onOpenChange={() => { setViewingImage(null); if (imageBlob) URL.revokeObjectURL(imageBlob); setImageBlob(null); }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Proof Image</DialogTitle>
          </DialogHeader>
          {imageBlob && (
            <img src={imageBlob} alt="Proof" className="w-full rounded-lg max-h-[60vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Analysis Dialog */}
      <Dialog open={!!viewingReceipt} onOpenChange={() => setViewingReceipt(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Receipt Analysis (AI)</DialogTitle>
          </DialogHeader>
          {viewingReceipt?.data && (
            <div className="space-y-3 py-2">
              {viewingReceipt.data.parse_error ? (
                <pre className="text-xs text-zinc-400 whitespace-pre-wrap bg-zinc-900 p-3 rounded-lg">{viewingReceipt.data.raw_text}</pre>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass-card rounded-lg p-3">
                      <p className="text-[10px] text-zinc-500">Store</p>
                      <p className="text-sm text-white font-bold">{viewingReceipt.data.store_name || 'N/A'}</p>
                    </div>
                    <div className="glass-card rounded-lg p-3">
                      <p className="text-[10px] text-zinc-500">Total</p>
                      <p className="text-sm text-emerald-400 font-bold">${viewingReceipt.data.total || 'N/A'}</p>
                    </div>
                    <div className="glass-card rounded-lg p-3">
                      <p className="text-[10px] text-zinc-500">Date</p>
                      <p className="text-sm text-white">{viewingReceipt.data.date || 'N/A'}</p>
                    </div>
                    <div className="glass-card rounded-lg p-3">
                      <p className="text-[10px] text-zinc-500">Order #</p>
                      <p className="text-sm text-white font-mono">{viewingReceipt.data.order_number || 'N/A'}</p>
                    </div>
                  </div>
                  {viewingReceipt.data.items?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-zinc-400 mb-2">Items</p>
                      <div className="space-y-1">
                        {viewingReceipt.data.items.map((item, i) => (
                          <div key={i} className="flex justify-between px-3 py-1.5 rounded bg-white/[0.02] text-xs">
                            <span className="text-zinc-300">{item.name}</span>
                            <span className="text-zinc-400 font-mono">${item.price}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
